import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';

const SearchableMultiSelect = ({
  options,
  selectedOptions,
  onChange,
  isDisabled,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  const handleToggleOption = (optionId) => {
    const newSelection = selectedOptions.includes(optionId)
      ? selectedOptions.filter((id) => id !== optionId)
      : [...selectedOptions, optionId];
    onChange(newSelection);
  };

  const filteredOptions = options.filter((option) =>
    option.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [wrapperRef]);

  const selectedLabels = options
    .filter((opt) => selectedOptions.includes(opt.id))
    .map((opt) => opt.title);

  return (
    <div ref={wrapperRef} className="relative">
      <label className="block text-sm font-medium text-slate-700 mb-1">
        Assign Papers
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          disabled={isDisabled}
          className="w-full p-2 border border-slate-300 rounded-lg bg-white flex justify-between items-center text-left disabled:bg-slate-100 disabled:cursor-not-allowed"
        >
          <span className="truncate text-slate-700">
            {selectedOptions.length > 0
              ? `${selectedOptions.length} paper(s) selected`
              : 'Select papers...'}
          </span>
          <ChevronDown
            size={18}
            className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {isOpen && !isDisabled && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg">
          <div className="p-2">
            <input
              type="text"
              placeholder="Search papers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-2 border border-slate-300 rounded-lg"
            />
          </div>
          <ul className="max-h-60 overflow-y-auto custom-scrollbar p-2">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <li key={option.id}>
                  <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-100 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedOptions.includes(option.id)}
                      onChange={() => handleToggleOption(option.id)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-slate-800">
                      {option.title}
                    </span>
                  </label>
                </li>
              ))
            ) : (
              <li className="p-2 text-sm text-slate-500 text-center">
                No papers found.
              </li>
            )}
          </ul>
        </div>
      )}
      {selectedOptions.length > 0 && !isDisabled && (
        <div className="pt-2 flex flex-wrap gap-1">
          {selectedLabels.map((label) => (
            <span
              key={label}
              className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full"
            >
              {label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchableMultiSelect;
